/**
 * Copyright © 2026 Adnuntius AS.
 */
import {BsDatepickerConfig, BsDatepickerDirective, BsLocaleService} from 'ngx-bootstrap/datepicker';
import {Component, forwardRef, Inject, Input, OnInit} from '@angular/core';
import {ControlValueAccessor, NG_VALUE_ACCESSOR} from '@angular/forms';
import {TranslateService} from '@ngx-translate/core';
import {DateTime as LuxonDateTime} from 'luxon';

import template from './date-time-picker.component.html';
import {applyNetworkCalendarWeekStart, toBsDateInputFormat} from './bs-datepicker-locale';
import {AppLocaleService} from '../../locale/app-locale.service';
import {getIntlLocaleData, AngularLocaleData} from '../../locale/intl-locale-data';
import {LocalNetworkProfile} from '../../../../components/session/local-network-profile';

@Component({
  standalone: false,
  selector: 'adn-date-time-picker',
  template,
  providers: [{
    provide: NG_VALUE_ACCESSOR,
    useExisting: forwardRef(() => DateTimePickerComponent),
    multi: true
  }]
})
export class DateTimePickerComponent implements ControlValueAccessor, OnInit {
  private static readonly TODAY_HIGHLIGHT_CLASS = 'adn-bs-datepicker-today';

  @Input() name = 'dateTime';
  @Input() dateOnly = false;
  @Input() timeZoneMode: 'network' | 'utc' = 'network';
  @Input() startOfDayForFutureDates = false;

  disabled = false;
  dateValue: Date | null = null;
  timeValue: Date | null = null;
  datePlaceholder: string;
  bsConfig: Partial<BsDatepickerConfig>;
  private readonly localeData: AngularLocaleData;
  private lastWrittenTimestamp: number | null = null;
  private suppressEmit = false;
  private writeGeneration = 0;
  private suppressEmitTimer: ReturnType<typeof setTimeout> | null = null;

  private onChange: (value: Date | null) => void = () => {
  };
  private onTouched: () => void = () => {
  };

  constructor(
    private readonly appLocale: AppLocaleService,
    @Inject(LocalNetworkProfile) private readonly LocalNetworkProfile: any,
    private readonly bsLocaleService: BsLocaleService,
    private readonly translate: TranslateService
  ) {
    this.localeData = getIntlLocaleData(this.appLocale.getLocaleId());
    this.datePlaceholder = this.toAngularDateFormat(this.localeData.DATETIME_FORMATS.shortDate);
    this.refreshBsConfig();
  }

  ngOnInit(): void {
    applyNetworkCalendarWeekStart(
      this.bsLocaleService,
      this.LocalNetworkProfile.getDefaults()?.startDayCalendar,
      this.localeData
    );
  }

  writeValue(value: Date | string | null | undefined): void {
    const generation = ++this.writeGeneration;
    this.beginSuppressEmit();

    const normalizedValue = value ? this.normalizeDateTime(this.toDisplayDate(value)) : null;
    if (!normalizedValue || Number.isNaN(normalizedValue.getTime())) {
      this.lastWrittenTimestamp = null;
      this.dateValue = null;
      this.timeValue = null;
      this.refreshBsConfig();
      this.endSuppressEmit(generation);
      return;
    }

    this.dateValue = new Date(normalizedValue);
    this.timeValue = this.dateOnly
      ? this.getDefaultTimeForDate(this.dateValue)
      : new Date(normalizedValue);
    this.refreshBsConfig();
    this.lastWrittenTimestamp = this.toModelDate(this.dateValue, this.timeValue).getTime();
    this.endSuppressEmit(generation);
  }

  registerOnChange(fn: (value: Date | null) => void): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    this.disabled = isDisabled;
  }

  onDateValueChange(value: Date | null | undefined): void {
    const nextValue = value ? new Date(value) : null;
    if (this.sameTimestamp(this.dateValue, nextValue)) {
      return;
    }
    this.dateValue = nextValue;
    if (!this.dateValue) {
      this.timeValue = null;
    } else if (this.startOfDayForFutureDates && this.isAfterToday(this.dateValue)) {
      this.timeValue = this.getStartOfDay(this.dateValue);
    } else if (!this.timeValue) {
      this.timeValue = this.getDefaultTimeForDate(this.dateValue);
    }
    this.refreshBsConfig();
    this.emitValue();
  }

  refreshBsConfig(): void {
    this.bsConfig = this.createBsConfig();
  }

  onDatePickerShown(datePicker: BsDatepickerDirective): void {
    applyNetworkCalendarWeekStart(
      this.bsLocaleService,
      this.LocalNetworkProfile.getDefaults()?.startDayCalendar,
      this.localeData
    );
    this.refreshBsConfig();
    setTimeout(() => this.wireClearButton(datePicker));
  }

  onDatePickerHidden(event: unknown): void {
    const target = (event as Event)?.target as HTMLElement | undefined;
    target?.blur?.();
  }

  onTimeValueChange(value: Date | null | undefined): void {
    const nextValue = value ? new Date(value) : null;
    if (this.sameTimestamp(this.timeValue, nextValue)) {
      return;
    }
    this.timeValue = nextValue;
    this.emitValue();
  }

  private emitValue(): void {
    if (this.suppressEmit) {
      return;
    }

    this.onTouched();

    if (!this.dateValue) {
      if (this.lastWrittenTimestamp === null) {
        return;
      }
      this.lastWrittenTimestamp = null;
      this.onChange(null);
      return;
    }

    const nextValue = this.toModelDate(this.dateValue, this.timeValue);
    const nextTimestamp = nextValue.getTime();
    if (this.lastWrittenTimestamp === nextTimestamp) {
      return;
    }

    this.lastWrittenTimestamp = nextTimestamp;
    this.onChange(nextValue);
  }

  private beginSuppressEmit(): void {
    this.suppressEmit = true;
    if (this.suppressEmitTimer !== null) {
      clearTimeout(this.suppressEmitTimer);
      this.suppressEmitTimer = null;
    }
  }

  private endSuppressEmit(generation: number): void {
    this.suppressEmitTimer = setTimeout(() => {
      if (this.writeGeneration === generation) {
        this.suppressEmit = false;
      }
      this.suppressEmitTimer = null;
    });
  }

  private sameTimestamp(left: Date | null, right: Date | null): boolean {
    if (!left && !right) {
      return true;
    }
    if (!left || !right) {
      return false;
    }
    return left.getTime() === right.getTime();
  }

  private normalizeDateTime(value: Date): Date {
    const normalized = new Date(value);
    normalized.setSeconds(0, 0);
    return normalized;
  }

  private getDefaultTimeForDate(dateValue: Date): Date {
    if (!this.dateOnly && this.isToday(dateValue)) {
      return this.getDisplayNow();
    }

    return this.getStartOfDay(dateValue);
  }

  private isToday(value: Date): boolean {
    const today = this.getDisplayNow();
    return value.getFullYear() === today.getFullYear()
      && value.getMonth() === today.getMonth()
      && value.getDate() === today.getDate();
  }

  private isAfterToday(value: Date): boolean {
    return this.getStartOfDay(value).getTime() > this.getStartOfDay(this.getDisplayNow()).getTime();
  }

  private getStartOfDay(value: Date): Date {
    const startOfDay = new Date(value);
    startOfDay.setHours(0, 0, 0, 0);
    return startOfDay;
  }

  private createBsConfig(): Partial<BsDatepickerConfig> {
    return {
      dateInputFormat: toBsDateInputFormat(this.localeData.DATETIME_FORMATS.shortDate),
      dateCustomClasses: [{
        date: this.getTodayForHighlight(),
        classes: [DateTimePickerComponent.TODAY_HIGHLIGHT_CLASS]
      }],
      selectFromOtherMonth: true,
      showTodayButton: true,
      showClearButton: true,
      todayPosition: 'left',
      clearPosition: 'right',
      todayButtonLabel: this.translate.instant('button.today'),
      clearButtonLabel: this.translate.instant('button.clear')
    };
  }

  private toAngularDateFormat(shortDateFormat: string): string {
    const angularFormat = shortDateFormat || 'd/M/yy';
    return angularFormat.replace(/yy/g, 'y');
  }


  private toDisplayDate(value: Date | string): Date {
    const sourceDateTime = this.toSourceDateTime(value);
    if (!sourceDateTime.isValid) {
      return new Date(NaN);
    }

    return this.toUiDate(sourceDateTime.setZone(this.getEffectiveTimezone()));
  }

  private toModelDate(dateValue: Date, timeValue: Date | null): Date {
    const timeToUse = this.dateOnly
      ? this.getDefaultTimeForDate(dateValue)
      : (timeValue || dateValue);
    return LuxonDateTime.fromObject({
      year: dateValue.getFullYear(),
      month: dateValue.getMonth() + 1,
      day: dateValue.getDate(),
      hour: timeToUse.getHours(),
      minute: timeToUse.getMinutes(),
      second: 0,
      millisecond: 0
    }, {zone: this.getEffectiveTimezone()}).toJSDate();
  }

  private getDisplayNow(): Date {
    return this.toUiDate(LuxonDateTime.now().setZone(this.getEffectiveTimezone()));
  }

  private getTodayForHighlight(): Date {
    const today = this.getDisplayNow();
    today.setHours(0, 0, 0, 0);
    return today;
  }

  private wireClearButton(datePicker: BsDatepickerDirective): void {
    const container = this.getDatepickerContainer(datePicker);
    const clearButton = container?.querySelector('.btn-clear-wrapper button') as HTMLButtonElement | null;
    if (!clearButton) {
      return;
    }

    clearButton.addEventListener('click', (event) => {
      event.preventDefault();
      event.stopImmediatePropagation();
      this.clearDateSelection(datePicker);
    }, true);
  }

  private clearDateSelection(datePicker: BsDatepickerDirective): void {
    this.onDateValueChange(null);
    // Do not call ngx-bootstrap clearDate(): SELECT(undefined) with an existing
    // selectedTime runs copyTime(undefined, time) → undefined.setHours(...).
    // Nulling bsValue skips that path; hide() closes the popover.
    datePicker.bsValue = null;
    datePicker.hide();
  }

  private getDatepickerContainer(datePicker: BsDatepickerDirective): HTMLElement | null {
    return this.getDatepickerContainerInstance(datePicker)?._element?.nativeElement || null;
  }

  private getDatepickerContainerInstance(datePicker: BsDatepickerDirective): any {
    return (datePicker as any)._datepickerRef?.instance;
  }

  private toSourceDateTime(value: Date | string): LuxonDateTime {
    if (value instanceof Date) {
      return LuxonDateTime.fromJSDate(value);
    }

    if (typeof value === 'string' && this.isOffsetlessDateTimeString(value)) {
      return this.parseDateTimeString(value, 'utc');
    }

    return this.parseDateTimeString(value);
  }

  private isOffsetlessDateTimeString(value: string): boolean {
    return /[T ]\d{2}:\d{2}/.test(value) && !/(Z|[+-]\d{2}(?::?\d{2})?)$/i.test(value);
  }

  private parseDateTimeString(value: string, zone?: string): LuxonDateTime {
    const options = zone ? {zone, setZone: true} : {setZone: true};
    const parsedIso = LuxonDateTime.fromISO(value, options);
    if (parsedIso.isValid) {
      return parsedIso;
    }

    const parsedSql = LuxonDateTime.fromSQL(value, options);
    if (parsedSql.isValid) {
      return parsedSql;
    }

    return LuxonDateTime.invalid('Invalid date time');
  }

  private toUiDate(value: LuxonDateTime): Date {
    return new Date(
      value.year,
      value.month - 1,
      value.day,
      value.hour,
      value.minute,
      0,
      0
    );
  }

  private getEffectiveTimezone(): string {
    if (this.timeZoneMode === 'utc') {
      return 'UTC';
    }

    return this.LocalNetworkProfile.get('timeZone') || 'utc';
  }
}
